import type { CostBenefitUnmatchedHolding } from "@repo/shared/types";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./unmatched-holdings-warning.i18n";

type UnmatchedHoldingsWarningProps = {
  holdings: CostBenefitUnmatchedHolding[];
};

/**
 * QL-304 principle — holdings that could not be mapped to a collection product
 * are never silently dropped: they are named here, with their share, so the
 * reader knows what the figures above leave out.
 */
export function UnmatchedHoldingsWarning({ holdings }: UnmatchedHoldingsWarningProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();
  if (holdings.length === 0) return null;

  return (
    <Alert variant="warning" data-testid="cost-benefit-unmatched-warning">
      <AlertTriangle />
      <AlertDescription>
        <p>{t.unmatchedTitle(holdings.length)}</p>
        <ul className="mt-1 list-disc pl-5">
          {holdings.map((h) => (
            <li key={`${h.name}|${h.isin ?? ""}|${h.amount}|${h.weightPct}`}>
              {h.name}
              {h.isin && h.isin !== h.name ? ` (${h.isin})` : ""}
              {" — "}
              {formatNumber(h.weightPct, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
